/**
 * Generic IndexedDB utility — DRY factory for all IDB usage in the app.
 *
 * Store names are type-safe: TypeScript will error if you pass an invalid store name.
 *
 * Usage:
 *   const db = createIDB({ name: 'MyDB', version: 1, stores: { users: {}, posts: {} } });
 *   await db.put('users', key, value);  // ✅
 *   await db.put('invalid', key, value); // ❌ TypeScript error
 */

// ---- Types ----

export interface IDBIndex {
  name: string;
  keyPath: string;
  unique?: boolean;
}

export interface IDBStoreConfig {
  /** If set, objects stored must have this property as key */
  keyPath?: string;
  autoIncrement?: boolean;
  indexes?: IDBIndex[];
}

export interface IDBConfig<S extends string> {
  name: string;
  version: number;
  stores: Record<S, IDBStoreConfig>;
}

export interface IDBInstance<S extends string> {
  /** Get a value by key. Returns null if not found. */
  get: <T>(store: S, key: IDBValidKey) => Promise<T | null>;
  /** Put (upsert) a value. If store has keyPath, key param is ignored. */
  put: (store: S, key: IDBValidKey | null, value: unknown) => Promise<void>;
  /** Delete a value by key. */
  del: (store: S, key: IDBValidKey) => Promise<void>;
  /** Get all values from a store, optionally filtered by index + key. */
  getAll: <T>(
    store: S,
    indexName?: string,
    query?: IDBValidKey,
  ) => Promise<T[]>;
  /** Get all keys from an index matching a query. */
  getAllKeys: (
    store: S,
    indexName: string,
    query: IDBValidKey,
  ) => Promise<IDBValidKey[]>;
  /** Clear all data in a store. */
  clear: (store: S) => Promise<void>;
  /** Run a custom operation with the raw IDBDatabase. DB is auto-closed after. */
  withDB: <T>(fn: (db: IDBDatabase) => Promise<T>) => Promise<T>;
}

// ---- Helpers ----

/** Convert an IDBRequest into a Promise. */
export const idbRequest = <T>(request: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

// ---- Factory ----

export function createIDB<S extends string>(
  config: IDBConfig<S>,
): IDBInstance<S> {
  const openDB = (): Promise<IDBDatabase> =>
    new Promise((resolve, reject) => {
      const request = indexedDB.open(config.name, config.version);

      request.onupgradeneeded = () => {
        const db = request.result;
        for (const [storeName, storeConfig] of Object.entries<IDBStoreConfig>(
          config.stores,
        )) {
          if (!db.objectStoreNames.contains(storeName)) {
            const store = db.createObjectStore(storeName, {
              keyPath: storeConfig.keyPath,
              autoIncrement: storeConfig.autoIncrement,
            });
            for (const idx of storeConfig.indexes ?? []) {
              store.createIndex(idx.name, idx.keyPath, {
                unique: idx.unique ?? false,
              });
            }
          }
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

  const withDB = async <T>(fn: (db: IDBDatabase) => Promise<T>): Promise<T> => {
    const db = await openDB();
    try {
      return await fn(db);
    } finally {
      db.close();
    }
  };

  const get = <T>(store: S, key: IDBValidKey): Promise<T | null> =>
    withDB(async db => {
      const tx = db.transaction(store, 'readonly');
      const result = await idbRequest(tx.objectStore(store).get(key));
      return result ?? null;
    });

  const put = (
    store: S,
    key: IDBValidKey | null,
    value: unknown,
  ): Promise<void> =>
    withDB(async db => {
      const tx = db.transaction(store, 'readwrite');
      // If store has keyPath, key is embedded in value — pass undefined
      if (config.stores[store]?.keyPath) {
        await idbRequest(tx.objectStore(store).put(value));
      } else {
        await idbRequest(tx.objectStore(store).put(value, key!));
      }
    });

  const del = (store: S, key: IDBValidKey): Promise<void> =>
    withDB(async db => {
      const tx = db.transaction(store, 'readwrite');
      await idbRequest(tx.objectStore(store).delete(key));
    });

  const getAll = <T>(
    store: S,
    indexName?: string,
    query?: IDBValidKey,
  ): Promise<T[]> =>
    withDB(async db => {
      const tx = db.transaction(store, 'readonly');
      const objectStore = tx.objectStore(store);
      if (indexName) {
        const index = objectStore.index(indexName);
        return idbRequest(index.getAll(query));
      }
      return idbRequest(objectStore.getAll());
    });

  const getAllKeys = (
    store: S,
    indexName: string,
    query: IDBValidKey,
  ): Promise<IDBValidKey[]> =>
    withDB(async db => {
      const tx = db.transaction(store, 'readonly');
      return idbRequest(
        tx.objectStore(store).index(indexName).getAllKeys(query),
      );
    });

  const clear = (store: S): Promise<void> =>
    withDB(async db => {
      const tx = db.transaction(store, 'readwrite');
      await idbRequest(tx.objectStore(store).clear());
    });

  return { get, put, del, getAll, getAllKeys, clear, withDB };
}
